import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Layout, theme, Button, message, Input } from "antd";
import { TeamOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import LoadingOverlay from "react-loading-overlay-ts";
import dayjs from "dayjs";
import "dayjs/locale/en"; // Import locale if needed
import utc from "dayjs/plugin/utc"; // Import UTC plugin if needed
import { normalizePolicyAddonsForForm } from "../../utils/addonHelpers";

import TopBar from "../../components/TopBar";
import SideBar from "../../components/SideBar";
import AddMemberModal from "../../components/ModalForm/Member/AddMemberModal";
import AddPaymentMethodModal from "../../components/ModalForm/Member/AddPaymentMethodModal";
import GenerateDocumentModal from "../../components/ModalForm/Member/GenerateDocumentModal";
import SendDocumentModal from "../../components/ModalForm/Member/SendDocumentModal";
import { LapsePolicyModal, CancelPolicyModal, ActivatePolicyModal } from "../../components/ModalForm";
import EditPaymentMethodModal from "../../components/ModalForm/Member/EditPaymentMethodModal";
import EditMemberModal from "../../components/ModalForm/Member/EditMemberModal";
import { MemberTable } from "../../components/Table";

import { AuthContext } from "../../context/AuthContext";
import { useFormContext } from "../../hooks/useFormContext";
import { TourContext } from "../../context/TourContext";

import {
  getMemberApi,
  getCompanyMembersApi,
  addMemberApi,
  editMemberApi,
  removeMemberApi,
  lapseMemberPolicyApi,
  activateMemberPolicyApi,
  searchMembersApi,
  loadMemberPremiumApi,
} from "../../services/Member/MemberApis";
import {
  addPaymentApi,
  editPaymentApi,
} from "../../services/Payment/PaymentApis";
import {
  generateDocumentApi,
  sendDocumentApi,
} from "../../services/Document/DocumentApis";
import LoadPremiumModal from "../../components/ModalForm/Member/LoadPremiumModal";
import { validateSACellphone } from "../../utils/systemValidation";
import { validateSelectedDocumentsForSend } from "../../utils/documentCompliance";
import { toast } from "../../utils/toast";

const { Content } = Layout;
const { Search } = Input;
dayjs.extend(utc);

const MembersPage: React.FC = () => {
  const { currentGroup, currentUser } = useContext(AuthContext);
  const { refAddMember, markPageAsVisited, pages, setOpen, setPageLoaded } =
    useContext(TourContext);
  const { setPolicyInfo, setProductInfo } = useFormContext();

  const navigate = useNavigate();

  const [members, setMembers] = useState<any>([]);
  const [member, setMember] = useState<any>({});
  const [payment, setPayment] = useState<any>({});
  const [memberId, setMemberId] = useState<string>("");
  const [filtered, setFiltered] = useState<any>([]);
  const [searchActive, setSearchActive] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>(() => sessionStorage.getItem("membersSearch") || "");
  const [show, setShow] = useState(false);
  const [state, setState] = useState({
    add: false,
    payment: false,
    document: false,
    update: false,
    send: false,
    lapse: false,
    cancel: false,
    premium: false,
    activate: false,
  });
  const [isActive, setActive] = useState(true);

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const handleManage = (id: string) => {
    navigate(`/members/${id}`);
  };

  const showModal = () => {
    setShow(!show);
    setState({
      add: true,
      payment: false,
      document: false,
      update: false,
      send: false,
      lapse: false,
      cancel: false,
      premium: false,
      activate: false,
    });
  };

  const showPaymentModal = async (id: string) => {
    await getMemberApi(id)
      .then((res) => {
        const formData = res.data.PaymentType && {
          ...res.data.PaymentType,
          AccountNumber: res.data.PaymentType.AccountNumber,
          AccountType:
            res.data.PaymentType.AccountType === "Savings"
              ? "Savings"
              : "Current",
          BranchCode: res.data.PaymentType.BranchCode,
          Amount: res.data.PaymentType.Amount,
          StartDate: dayjs(res.data.PaymentType.StartDate, "YYYY-MM-DD"),
          PayAtAccountNumber: res.data.PaymentType.PayAtAccountNumber,
        };

        setPayment(formData);
        setShow(!show);
        setMemberId(id);
        setState({
          add: false,
          payment: true,
          document: false,
          update: false,
          send: false,
          lapse: false,
          cancel: false,
          premium: false,
          activate: false,
        });
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const showDocumentModal = (id: string) => {
    setShow(!show);
    setMemberId(id);
    setState({
      add: false,
      payment: false,
      document: true,
      update: false,
      send: false,
      lapse: false,
      cancel: false,
      premium: false,
      activate: false,
    });
  };

  const showSendModal = (id: string) => {
    setShow(!show);
    setMemberId(id);
    setState({
      add: false,
      payment: false,
      document: false,
      update: false,
      send: true,
      lapse: false,
      cancel: false,
      premium: false,
      activate: false,
    });
  };

  const showEditModal = async (id: string) => {
    return await getMemberApi(id)
      .then((res) => {
        setShow(!show);

        res.data.Policy.Product && setProductInfo(res.data.Policy.Product);

        if (res.data.Beneficiary !== null) {
          const formData = {
            ...res.data,
            ...res.data.Beneficiary,
            id: res.data.id,
            DateOfBirth: dayjs(res.data.DateOfBirth),
            Identity: {
              value: res.data.IdType,
              label:
                res.data.IdType === 1
                  ? "South African ID"
                  : "South African Passport",
            },
            InceptionDate: dayjs(res.data.InceptionDate),
            PolicyNumber: res.data.Policy.PolicyNumber,
            PolicyUserID: res.data.Policy.User?.id || res.data.Policy.UserID,
            ProductType: {
              value: res.data.Policy.Product.PlanCategory.Name,
              label: res.data.Policy.Product.PlanCategory.Name,
            },
            Product: {
              value: res.data.Policy.Product.id,
              label: res.data.Policy.Product.ProductName,
            },
            Premium: res.data.TotalPremium,
            SourceOfFunds: {
              value: res.data.SourceOfFunds,
              label: res.data.SourceOfFunds,
            },
            CoveredLive: res.data.CoveredLive.map((cl: any) => {
              return {
                id: cl.id,
                CoveredType: cl.CoveredType,
                CoveredName: cl.CoveredName,
                CoveredSurname: cl.CoveredSurname,
                CoveredAmount: cl.CoveredAmount,
                CoveredDateOfBirth: dayjs(cl.CoveredDateOfBirth)
                  .startOf("day")
                  .format("YYYY-MM-DD"),
                CoveredIdNumber: cl.CoveredIdNumber,
                ExtendedProduct: cl.ExtendedProduct
                  ? {
                      value: cl.ExtendedProduct.id,
                      label: cl.ExtendedProduct.ProductName,
                    }
                  : null, // Set ExtendedProduct to null if it doesn't exist
              };
            }),
            BenDateOfBirth: dayjs(res.data.Beneficiary.BenDateOfBirth),
            Addons: normalizePolicyAddonsForForm(res.data.Policy?.Addon || []),
          };

          setMember(formData);
        } else {
          const formData = {
            ...res.data,
            id: res.data.id,
            DateOfBirth: dayjs(res.data.DateOfBirth),
            Identity: {
              value: res.data.IdType,
              label:
                res.data.IdType === 1
                  ? "South African ID"
                  : "South African Passport",
            },
            InceptionDate: dayjs(res.data.InceptionDate),
            PolicyUserID: res.data.Policy.User?.id || res.data.Policy.UserID,
            Group: {
              value: res.data.Group.id,
              label: res.data.Group.GroupName,
            },
            ProductType: {
              value: res.data.Policy.Product.PlanCategory.Name,
              label: res.data.Policy.Product.PlanCategory.Name,
            },
            Product: {
              value: res.data.Policy.Product.id,
              label: res.data.Policy.Product.ProductName,
            },
            SourceOfFunds: {
              value: res.data.SourceOfFunds,
              label: res.data.SourceOfFunds,
            },
            Addons: normalizePolicyAddonsForForm(res.data.Policy?.Addon || []),
            CoveredLive: res.data.CoveredLive.map((cl: any) => {
              return {
                id: cl.id,
                CoveredType: cl.CoveredType,
                CoveredName: cl.CoveredName,
                CoveredSurname: cl.CoveredSurname,
                CoveredAmount: cl.CoveredAmount,
                CoveredDateOfBirth: dayjs(cl.CoveredDateOfBirth)
                  .startOf("day")
                  .format("YYYY-MM-DD"),
                CoveredIdNumber: cl.CoveredIdNumber,
                ExtendedProduct: cl.ExtendedProduct
                  ? {
                      value: cl.ExtendedProduct.id,
                      label: cl.ExtendedProduct.ProductName,
                    }
                  : null, // Set ExtendedProduct to null if it doesn't exist
              };
            }),
          };

          setMember(formData);
        }

        setState({
          add: false,
          payment: false,
          document: false,
          update: true,
          send: false,
          lapse: false,
          cancel: false,
          premium: false,
          activate: false,
        });
      })
      .catch((err) => {
        message.error(err);
        console.log(err);
      });
  };

  const showLapseModal = (id: string) => {
    setShow(!show);
    setMemberId(id);
    setState({
      add: false,
      payment: false,
      document: false,
      update: false,
      send: false,
      lapse: true,
      cancel: false,
      premium: false,
      activate: false,
    });
  };

  const showCancelModal = (id: string) => {
    setShow(!show);
    setMemberId(id);
    setState({
      add: false,
      payment: false,
      document: false,
      update: false,
      send: false,
      lapse: false,
      cancel: true,
      premium: false,
      activate: false,
    });
  };

  const showPremiumModal = (id: string) => {
    setShow(!show);
    setMemberId(id);
    setState({
      add: false,
      payment: false,
      document: false,
      update: false,
      send: false,
      lapse: false,
      cancel: false,
      premium: true,
      activate: false,
    });
  };

  const showActivateModal = (id: string) => {
    const selected = members.find((m: any) => String(m.id) === String(id));
    setShow(!show);
    setMemberId(id);
    if (selected) setMember(selected);
    setState({
      add: false,
      payment: false,
      document: false,
      update: false,
      send: false,
      lapse: false,
      cancel: false,
      premium: false,
      activate: true,
    });
  };

  const handleAddMember = async (data: any) => {
    setActive(true);

    // Extract PolicyUserID if provided, otherwise use undefined to default to capturing user
    const policyUserId = data.PolicyUserID
      ? Number(data.PolicyUserID)
      : undefined;

    return await addMemberApi(data, currentUser.id, policyUserId)
      .then(async (res) => {
        // Refetch the group list so the new member has the same shape/StatusID as the table expects.
        // Appending res.data alone can omit fields and hide the member from Active/Inactive tabs.
        try {
          const groupId = String(
            typeof currentGroup === "object" && currentGroup != null
              ? (currentGroup as any).id
              : currentGroup
          );
          const listRes = await getCompanyMembersApi(groupId);
          let list = Array.isArray(listRes?.data) ? listRes.data : [];

          const created = res?.data
            ? {
                ...res.data,
                // Defaults so the row is visible even if create payload omits them
                StatusID: res.data.StatusID ?? res.data.Status?.id ?? 1,
                PolicyLapsed: res.data.PolicyLapsed ?? false,
                GroupID: res.data.GroupID ?? Number(groupId),
              }
            : null;

          // If the group list endpoint omits the new row (e.g. GroupID not persisted),
          // still surface it locally so capture isn't a silent no-op.
          if (created?.id != null && !list.some((m: any) => m.id === created.id)) {
            list = [created, ...list];
          }

          setMembers(list);

          if (created?.id != null) {
            const needle = created.IdNumber
              ? String(created.IdNumber)
              : String(created.id);
            const match = list.filter(
              (m: any) =>
                m.id === created.id ||
                (created.IdNumber != null &&
                  String(m.IdNumber) === String(created.IdNumber))
            );
            setSearchValue(needle);
            sessionStorage.setItem("membersSearch", needle);
            sessionStorage.setItem("membersPage", "1");
            sessionStorage.setItem("membersActiveTab", "1");
            setSearchActive(true);
            setFiltered(match.length > 0 ? match : [created]);

            if (match.length === 0) {
              message.warning(
                "Member was saved but may not be linked to this group yet. Showing it from the create response — please verify GroupID in the database."
              );
            }
          }
        } catch (refreshErr) {
          console.log(refreshErr);
          if (res?.data) {
            setMembers((prev: any[]) => [
              ...(Array.isArray(prev) ? prev : []),
              res.data,
            ]);
          }
        }

        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleAddPayment = async (id: string, type: string, data: any) => {
    setActive(true);
    return await addPaymentApi(id, type, data)
      .then((res) => {
        setActive(false);
        return res.data;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleGenerateDocument = async (id: string, type: string) => {
    if (!id) {
      return { success: false, message: "Please select a document." };
    }
    setActive(true);
    return await generateDocumentApi(id, type, memberId)
      .then((res) => {
        console.log(res);
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
        return { success: false, message: err?.message ?? "Failed to generate document." };
      });
  };

  const handleSend = async (memberId: string, data: any) => {
    let personalDocuments: any[] = [];
    const cachedMember = members.find(
      (m: any) => String(m.id) === String(memberId)
    );

    if (Array.isArray(cachedMember?.PersonalDocument)) {
      personalDocuments = cachedMember.PersonalDocument;
    } else {
      try {
        const res = await getMemberApi(memberId);
        personalDocuments = res.data?.PersonalDocument ?? [];
      } catch (err) {
        console.log(err);
      }
    }

    const validation = validateSelectedDocumentsForSend(
      data?.Documents,
      personalDocuments
    );

    if (!validation.isValid) {
      toast.error({
        title: "Cannot Send Documents",
        description:
          validation.message ??
          "One or more selected documents are missing a download link.",
        duration: 7,
      });
      return { success: false, message: validation.message };
    }

    setActive(true);
    return await sendDocumentApi(memberId, data, currentUser.id)
      .then((res) => {
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleLapse = async (memberId: string, data: any) => {
    setActive(true);

    return await lapseMemberPolicyApi(memberId, data, currentUser.id)
      .then(async (res) => {
        return await getCompanyMembersApi(String(currentGroup))
          .then((res) => {
            setMembers(Array.isArray(res?.data) ? res.data : []);
            setActive(false);
            return res;
          })
          .catch((err) => {
            setActive(false);
            console.log(err);
          });
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handlePremium = async (memberId: string, userId: any) => {
    setActive(true);

    return await loadMemberPremiumApi(memberId, currentUser.id)
      .then(async (res) => {
        return await getCompanyMembersApi(String(currentGroup))
          .then((res) => {
            setMembers(Array.isArray(res?.data) ? res.data : []);
            setActive(false);
            return res;
          })
          .catch((err) => {
            setActive(false);
            console.log(err);
          });
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleActivate = async (memberId: string, data: any) => {
    const selected = members.find(
      (m: any) => String(m.id) === String(memberId)
    );
    const validation = validateSACellphone(selected?.Cellphone);
    if (!validation.isValid) {
      message.error(validation.message || "Invalid cellphone");
      return { success: false, message: validation.message };
    }

    setActive(true);

    try {
      const res = await activateMemberPolicyApi(
        memberId,
        data,
        currentUser.id
      );
      if (res?.success === false) {
        setActive(false);
        message.error(res?.message || "Failed to activate policy.");
        return res;
      }
      const groupRes = await getCompanyMembersApi(String(currentGroup));
      setMembers(Array.isArray(groupRes?.data) ? groupRes.data : []);
      setActive(false);
      message.success("Policy activated.");
      return res ?? { success: true };
    } catch (err: any) {
      setActive(false);
      console.log(err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to activate policy.";
      message.error(msg);
      return { success: false, message: msg };
    }
  };

  const handleEditPayment = async (id: string, data: any) => {
    setActive(true);
    return await editPaymentApi(id, data)
      .then((res) => {
        setActive(false);
        return res.data;
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  };

  const handleUpdate = async (id: string, data: any, userId: any) => {
    setActive(true);

    // Extract PolicyUserID if provided for override, otherwise undefined to leave unchanged
    const policyUserId = data.PolicyUserID
      ? Number(data.PolicyUserID)
      : undefined;

    return await editMemberApi(id, data, userId, policyUserId)
      .then((res) => {
        const updatedMembers = members.map((member: any) => {
          if (id === member.id) {
            return { ...member, ...res.data };
          }
          return member;
        });
        setActive(false);
        setMembers(updatedMembers);
        return res;
      })
      .catch((err) => {
        setActive(false);
        message.error(err);
        console.log(err);
      });
  };

  const handleDelete = (id: string) => {
    setActive(true);
    removeMemberApi(id)
      .then((res) => {
        const deleted = res.data;
        setMembers(members.filter((member: any) => member.id !== deleted.id));
        setActive(false);
        return res;
      })
      .catch((err) => {
        setActive(false);
        message.error(err);
        console.log(err);
      });
  };

  const flattenObject = (obj: any): string => {
    let result = "";

    for (const key in obj) {
      if (typeof obj[key] === "object" && obj[key] !== null) {
        result += " " + flattenObject(obj[key]);
      } else {
        result += " " + String(obj[key]);
      }
    }

    return result;
  };

  const onSearch = async (e: any) => {
    const value = e.target.value;
    setSearchValue(value);

    if (value !== "") {
      sessionStorage.setItem("membersSearch", value);
      setSearchActive(true);
      const groupId = String(
        typeof currentGroup === "object" && currentGroup != null
          ? (currentGroup as any).id
          : currentGroup
      );

      // GET /member/group/:groupId/search/:term — already scoped to the selected group
      const result = await searchMembersApi(groupId, value);
      const filteredData = Array.isArray(result?.data) ? result.data : [];

      setFiltered(filteredData);

      // Merge search hits into the table when the list endpoint omitted them
      if (filteredData.length > 0) {
        setMembers((prev: any[]) => {
          const list = Array.isArray(prev) ? prev : [];
          const byId = new Map(list.map((m: any) => [m.id, m]));
          filteredData.forEach((m: any) => {
            if (m?.id != null && !byId.has(m.id)) byId.set(m.id, m);
          });
          return Array.from(byId.values());
        });
      }
    } else {
      sessionStorage.removeItem("membersSearch");
      setSearchActive(false);
      setFiltered([]);
    }
  };

  useEffect(() => {
    getCompanyMembersApi(String(currentGroup))
      .then((res) => {
        setMembers(Array.isArray(res?.data) ? res.data : []);
        setActive(false);
        setPageLoaded(true);
        setOpen(true);

        // Restore search if navigating back
        const stored = sessionStorage.getItem("membersSearch");
        if (stored) {
          setSearchActive(true);
          searchMembersApi(String(currentGroup), stored)
            .then((result: any) => {
              const filteredData = Array.isArray(result?.data)
                ? result.data
                : [];
              setFiltered(filteredData);
              if (filteredData.length > 0) {
                setMembers((prev: any[]) => {
                  const list = Array.isArray(prev) ? prev : [];
                  const byId = new Map(list.map((m: any) => [m.id, m]));
                  filteredData.forEach((m: any) => {
                    if (m?.id != null && !byId.has(m.id)) byId.set(m.id, m);
                  });
                  return Array.from(byId.values());
                });
              }
            })
            .catch(console.log);
        }
      })
      .catch((err) => {
        setActive(false);
        console.log(err);
      });
  }, [currentGroup]);

  useEffect(() => {
    markPageAsVisited("Dashboard");
  }, []);

  return (
    <LoadingOverlay active={isActive} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        <SideBar />
        <Layout className="site-layout">
          <TopBar />
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <h2>
                <TeamOutlined
                  style={{ paddingRight: "5px", color: "#f04f05" }}
                />
                Members
              </h2>
              <div className="interactions" ref={refAddMember}>
                <Button onClick={showModal} size="large">
                  <UsergroupAddOutlined style={{ paddingRight: "5px" }} />
                  Add Member
                </Button>
                <div>
                  <Search
                    placeholder="Search"
                    size="large"
                    value={searchValue}
                    onChange={(e) => onSearch(e)}
                    style={{ width: "100%", margin: "24px 0" }}
                  />
                </div>

                {state.add && (
                  <AddMemberModal
                    show={show}
                    showModal={showModal}
                    handleAdd={handleAddMember}
                  />
                )}

                {state.payment &&
                  (payment?.id ? (
                    <EditPaymentMethodModal
                      show={show}
                      payment={payment}
                      showModal={showModal}
                      handleEdit={handleEditPayment}
                      memberId={memberId}
                    />
                  ) : (
                    <AddPaymentMethodModal
                      show={show}
                      showModal={showModal}
                      handleAdd={handleAddPayment}
                      handleEdit={handleEditPayment}
                      memberId={memberId}
                    />
                  ))}

                {state.document && (
                  <GenerateDocumentModal
                    show={show}
                    showModal={showModal}
                    handleGenerate={handleGenerateDocument}
                    memberId={memberId}
                  />
                )}

                {state.send && (
                  <SendDocumentModal
                    show={show}
                    showModal={showModal}
                    handleSend={handleSend}
                    memberId={memberId}
                  />
                )}

                {state.update && (
                  <EditMemberModal
                    show={show}
                    member={member}
                    showModal={showModal}
                    handleUpdate={handleUpdate}
                  />
                )}

                {state.lapse && (
                  <LapsePolicyModal
                    show={show}
                    showModal={showModal}
                    handleLapse={handleLapse}
                    memberId={memberId}
                  />
                )}

                {state.cancel && (
                  <CancelPolicyModal
                    show={show}
                    showModal={showModal}
                    handleCancel={handleLapse}
                    memberId={memberId}
                  />
                )}

                {state.premium && (
                  <LoadPremiumModal
                    show={show}
                    showModal={showModal}
                    handlePremium={handlePremium}
                    memberId={memberId}
                  />
                )}

                {state.activate && (
                  <ActivatePolicyModal
                    show={show}
                    showModal={showModal}
                    handleActivate={handleActivate}
                    memberId={memberId}
                    currentInceptionDate={member?.InceptionDate}
                  />
                )}
              </div>

              <MemberTable
                members={members}
                showPaymentModal={showPaymentModal}
                showDocumentModal={showDocumentModal}
                showSendModal={showSendModal}
                showEditModal={showEditModal}
                showLapseModal={showLapseModal}
                showCancelModal={showCancelModal}
                showPremiumModal={showPremiumModal}
                showActivateModal={showActivateModal}
                handleDelete={handleDelete}
                handleManage={handleManage}
                handleSend={handleSend}
                handleActivate={handleActivate}
                filtered={filtered}
                searchActive={searchActive}
              />
            </div>
          </Content>
        </Layout>
      </Layout>
    </LoadingOverlay>
  );
};

export default MembersPage;
